import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { FormPreviewButton } from '@risksmart-app/components/src/form-builder/FormPreviewButton';
import { useFormBuilderStore } from '@risksmart-app/components/src/form-builder/store/useFormBuilderStore';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import ControlledTabs from 'src/components/controlled-tabs/ControlledTabs';
import Loading from 'src/components/loading';

import { useGetQuestionnaireTemplateVersionById } from '@/hooks/queries/questionnaire-template-version/useGetQuestionnaireTemplateVersionById';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { PageLayout } from '../../../../../../layouts';
import { useTabs } from './useTabs';

interface Props {
  activeTabId: 'details';
}

const QuestionnaireTemplateVersionUpdatePage: FC<Props> = ({ activeTabId }) => {
  const tabs = useTabs();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const setIsPreviewingForm = useFormBuilderStore((s) => s.setIsPreviewingForm);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'questionnaire_template_versions',
  });
  const questionnaireTemplateVersionId = useGetGuidParam(
    'questionnaireTemplateVersionId'
  );

  const { data: questionnaireTemplateVersionData, loading } =
    useGetQuestionnaireTemplateVersionById({
      queryArgs: { id: questionnaireTemplateVersionId },
      shouldSkip: !questionnaireTemplateVersionId,
    });

  if (loading) {
    return (
      <PageLayout>
        <Loading />
      </PageLayout>
    );
  }

  if (
    questionnaireTemplateVersionData?.questionnaire_template_version?.Id !==
    questionnaireTemplateVersionId
  ) {
    throw new PageNotFound(
      `Version with id ${questionnaireTemplateVersionId} not found`
    );
  }

  const entity =
    questionnaireTemplateVersionData?.questionnaire_template_version;
  const title = entity?.parent?.Title ? `${entity.parent.Title}` : 'Version';
  const counter = entity?.Version ? `(${entity.Version})` : '';

  return (
    <PageLayout
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-171) — flag-gated, title + version counter.
      // Maps the legacy header's FormPreviewButton into the toolbar's primary
      // slot so the preview control isn't dropped when object_toolbar replaces
      // the header. The legacy FormPreviewButton is kept as the flag-off
      // fallback. (The version form owns its own save controls.)
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar
            config={{
              header: { title, counter: counter || undefined },
              actions: {
                primary: [
                  {
                    id: 'preview',
                    label: st('previewButtonLabel'),
                    iconName: 'file-06',
                    onClick: () => setIsPreviewingForm(true),
                  },
                ],
              },
            }}
          />
        ) : undefined
      }
      actions={<FormPreviewButton />}
    >
      <ControlledTabs
        activeTabId={activeTabId}
        tabs={tabs}
        variant={'container'}
      />
    </PageLayout>
  );
};

export default QuestionnaireTemplateVersionUpdatePage;
